/**
 * InterofficeChatTab — Agent-to-agent real-time messaging within the brokerage.
 *
 * Route: /agent-portal/interoffice-chat
 * Backend: POST /chat/team/chat        — send message (supports file attachments)
 *          GET  /chat/team/members      — brokerage team members
 *          POST /chat/conversations     — create conversation
 *          GET  /chat/conversations     — list conversations
 *          GET  /chat/conversations/:id/messages — message history
 * Real-time: Socket.io (new-message events)
 *
 * Port of the team-chat feature from the old agentkairos.com/chat into the
 * navy+gold Agent Portal hub. Same SplitPanel layout as other tabs.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperPlane,
  faUser,
  faUsers,
  faSpinner,
  faCircle,
  faComments,
  faPaperclip,
  faTimes,
  faImage,
  faFile,
  faDownload,
  faFilePdf,
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';
import { useAtom } from 'jotai';
import { user_info } from '../../jotai/UserJotaiInstance';
import toast from 'react-hot-toast';
import { useSocket } from '../../hooks/useSocket';
import SplitPanel from '../broker-intelligence/shared/SplitPanel';
import SearchBar from '../broker-intelligence/shared/SearchBar';
import ListItem from '../broker-intelligence/shared/ListItem';
import { colors, fonts } from '../broker-intelligence/shared/colors';

// Append auth token to attachment URLs so <img> tags can load them
const getAuthUrl = (url: string) => {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('accessToken');
  return token ? `${url}?token=${encodeURIComponent(token)}` : url;
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TeamMember {
  id: string;
  collaborator_id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  status: 'online' | 'away' | 'offline' | 'pending' | 'active';
  isPending: boolean;
  isAuthenticated: boolean;
  conversationId?: string;
  lastMessage?: string;
  lastMessageTime?: Date;
}

interface ChatMessage {
  id: string | number;
  content: string;
  sender: 'user' | 'collaborator';
  senderName?: string;
  timestamp: Date;
  attachments?: any[];
  status?: 'sending' | 'sent' | 'delivered';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const InterofficeChatTab: React.FC = () => {
  const [userInfo] = useAtom(user_info);

  // Team members
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [isSupportChat, setIsSupportChat] = useState(false);

  // Special "KairosIQ Support" contact — always available at top of list
  const supportMember: TeamMember = {
    id: '__kairosiq-support__',
    collaborator_id: '__kairosiq-support__',
    name: 'KairosIQ Support',
    firstName: 'KairosIQ',
    lastName: 'Support',
    email: 'support@kairosiq.ai',
    phone: '(520) 251-9595',
    role: 'Support Team',
    status: 'online',
    isPending: false,
    isAuthenticated: true,
  };
  const [memberSearch, setMemberSearch] = useState('');

  // Active chat
  const [activeMember, setActiveMember] = useState<TeamMember | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);

  // File attachments
  const [fileAttachments, setFileAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Socket.io for real-time messages
  const { joinConversation, leaveConversation } = useSocket({
    userId: userInfo?.id?.toString(),
    onNewMessage: useCallback((message: any) => {
      // Skip own messages
      if (message.metadata?.sender_id === userInfo?.id) return;

      if (conversationId && message.conversation_id === conversationId) {
        const newMsg: ChatMessage = {
          id: message.id,
          content: message.content,
          sender: 'collaborator',
          senderName: message.metadata?.sender_name,
          timestamp: new Date(message.timestamp),
          attachments: message.metadata?.attachments || [],
          status: 'delivered',
        };
        setMessages((prev) => {
          if (prev.some((m) => m.id === message.id)) return prev;
          return [...prev, newMsg];
        });
      }
    }, [conversationId, userInfo?.id]),
  });

  // Auto-scroll
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ---------------------------------------------------------------------------
  // Fetch team members
  // ---------------------------------------------------------------------------

  const fetchTeamMembers = useCallback(async () => {
    setLoadingMembers(true);
    try {
      const brokerageRes = await axiosInstance.get('/chat/team/members');
      if (!brokerageRes.data.success || !brokerageRes.data.brokerage) return;

      const brokerage = brokerageRes.data.brokerage;
      const agentsRes = await axiosInstance.get(`/broker/agents/${brokerage.id}`);

      if (agentsRes.data.success) {
        const members: TeamMember[] = agentsRes.data.agents
          .filter((a: any) => a.user_id !== userInfo?.id?.toString()) // exclude self
          .map((agent: any) => {
            const nameClean = agent.agent_full_name?.replace(' (Pending Invitation)', '') || '';
            const parts = nameClean.split(' ');
            return {
              id: agent.id,
              collaborator_id: agent.user_id || agent.id,
              name: nameClean,
              firstName: parts[0] || '',
              lastName: parts.slice(1).join(' ') || '',
              email: agent.agent_email,
              phone: agent.agent_phone || '',
              role: agent.member_status,
              status: agent.isAuthenticated ? 'active' : 'pending',
              isPending: !agent.isAuthenticated,
              isAuthenticated: agent.isAuthenticated || false,
            };
          });
        setTeamMembers(members);
      }
    } catch (err) {
      console.error('Failed to fetch team members:', err);
    } finally {
      setLoadingMembers(false);
    }
  }, [userInfo?.id]);

  useEffect(() => {
    fetchTeamMembers();
  }, [fetchTeamMembers]);

  // Poll for new messages in support chat every 3 seconds
  useEffect(() => {
    if (!isSupportChat || !conversationId) return;
    console.log('[SUPPORT-POLL] Starting poll for conversation:', conversationId);
    const pollMessages = async () => {
      try {
        // Fetch messages directly by conversation ID endpoint
        const res = await axiosInstance.get(`/chat/conversations/${conversationId}/messages`);
        const rawMsgs = res.data.messages || res.data || [];
        if (Array.isArray(rawMsgs) && rawMsgs.length > 0) {
          setMessages(prev => {
            if (rawMsgs.length === prev.length) return prev;
            console.log(`[SUPPORT-POLL] ${rawMsgs.length} msgs (was ${prev.length})`);
            return rawMsgs.map((m: any) => ({
              id: m.id,
              content: m.content || m.message,
              sender: m.role === 'user' ? 'user' as const : 'collaborator' as const,
              senderName: m.role === 'user' ? 'You' : 'KairosIQ Support',
              timestamp: new Date(m.timestamp || m.created_at),
              attachments: [],
            }));
          });
        }
      } catch {
        // Fallback: try the support-specific endpoint
        try {
          const userId = userInfo?.id?.toString() || localStorage.getItem('userId');
          if (userId) {
            const res = await axiosInstance.get(`/chat/support/conversations/${userId}`);
            if (res.data.success && res.data.messages) {
              setMessages(prev => {
                if (res.data.messages.length === prev.length) return prev;
                return res.data.messages.map((m: any) => ({
                  id: m.id,
                  content: m.content,
                  sender: m.role === 'user' ? 'user' as const : 'collaborator' as const,
                  senderName: m.role === 'user' ? 'You' : 'KairosIQ Support',
                  timestamp: new Date(m.timestamp),
                  attachments: [],
                }));
              });
            }
          }
        } catch {}
      }
    };
    pollMessages(); // Run immediately
    const interval = setInterval(pollMessages, 3000);
    return () => clearInterval(interval);
  }, [isSupportChat, conversationId, userInfo?.id]);

  // ---------------------------------------------------------------------------
  // Start / load conversation
  // ---------------------------------------------------------------------------

  const startChat = useCallback(async (member: TeamMember) => {
    setActiveMember(member);
    setMessages([]);
    setLoadingMessages(true);

    // Leave previous conversation room
    if (conversationId) {
      leaveConversation(conversationId);
    }

    const isSupport = member.id === '__kairosiq-support__';
    setIsSupportChat(isSupport);

    try {
      if (isSupport) {
        // Support chat — use support endpoints
        const userId = userInfo?.id?.toString();
        const res = await axiosInstance.get(`/chat/support/conversations/${userId}`);
        if (res.data.success) {
          const convId = res.data.conversation.id;
          setConversationId(convId);
          joinConversation(`support-${convId}`);
          const msgs: ChatMessage[] = (res.data.messages || []).map((m: any) => {
            const meta = typeof m.metadata === 'string' ? (() => { try { return JSON.parse(m.metadata); } catch { return {}; } })() : (m.metadata || {});
            return {
              id: m.id,
              content: m.content,
              sender: m.role === 'user' ? 'user' as const : 'collaborator' as const,
              senderName: m.role === 'user' ? 'You' : 'KairosIQ Support',
              timestamp: new Date(m.timestamp),
              attachments: meta.attachments || [],
            };
          });
          setMessages(msgs);
        }
      } else {
        // Team chat — existing flow
        const createRes = await axiosInstance.post('/chat/conversations', {
          agent_type: 'human-collaborator',
          agent_id: member.collaborator_id,
          title: `Chat with ${member.name}`,
        });

        const convId = createRes.data.conversationId || createRes.data.conversation?.id;
        if (!convId) throw new Error('No conversation ID returned');

        setConversationId(convId);
        joinConversation(convId);

        // Load existing messages
        const messagesRes = await axiosInstance.get(`/chat/conversations/${convId}/messages`);
        if (messagesRes.data.success || messagesRes.data.messages) {
          const msgs: ChatMessage[] = (messagesRes.data.messages || []).map((m: any) => ({
            id: m.id,
            content: m.content,
            sender: m.metadata?.sender_id === userInfo?.id?.toString() ? 'user' as const : 'collaborator' as const,
            senderName: m.metadata?.sender_name,
            timestamp: new Date(m.timestamp),
            attachments: m.metadata?.attachments || [],
          }));
          setMessages(msgs);
        }
      }
    } catch (err) {
      console.error('Failed to start chat:', err);
      toast.error('Failed to open conversation');
    } finally {
      setLoadingMessages(false);
    }
  }, [conversationId, userInfo?.id, joinConversation, leaveConversation]);

  // ---------------------------------------------------------------------------
  // Send message
  // ---------------------------------------------------------------------------

  const handleSend = useCallback(async () => {
    const text = inputMessage.trim();
    if ((!text && fileAttachments.length === 0) || !activeMember || !conversationId || sending) return;

    const tmpId = `tmp-${Date.now()}`;
    const userMsg: ChatMessage = {
      id: tmpId,
      content: text || `Sent ${fileAttachments.length} file(s)`,
      sender: 'user',
      senderName: `${userInfo?.user_first_name || ''} ${userInfo?.user_last_name || ''}`.trim(),
      timestamp: new Date(),
      status: 'sending',
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputMessage('');
    const filesToSend = [...fileAttachments];
    setFileAttachments([]);
    setSending(true);

    try {
      let response;
      if (isSupportChat && filesToSend.length === 0) {
        // Support chat — text only
        response = await axiosInstance.post('/chat/support/send', {
          conversationId,
          message: text,
        });
        if (response.data.success) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === tmpId ? { ...m, id: response.data.message.id, status: 'sent' as const } : m
            )
          );
        }
      } else if (isSupportChat && filesToSend.length > 0) {
        // Support chat with attachments — send directly via support endpoint
        const formData = new FormData();
        formData.append('conversationId', conversationId);
        formData.append('message', text || `Sent ${filesToSend.length} file(s)`);
        filesToSend.forEach((file) => {
          if (file.type.startsWith('image/')) {
            formData.append('images', file);
          } else {
            formData.append('files', file);
          }
        });
        response = await axiosInstance.post('/chat/support/send', formData);
        if (response?.data.success) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === tmpId ? { ...m, id: response.data.message.id, status: 'sent' as const, attachments: response.data.message.metadata?.attachments || [] } : m
            )
          );
        }
      } else if (filesToSend.length > 0) {
        const formData = new FormData();
        formData.append('conversation_id', conversationId);
        formData.append('message', text);
        formData.append('participant_id', activeMember.collaborator_id);
        filesToSend.forEach((file) => {
          if (file.type.startsWith('image/')) {
            formData.append('images', file);
          } else {
            formData.append('files', file);
          }
        });
        response = await axiosInstance.post('/chat/team/chat', formData);
      } else {
        response = await axiosInstance.post('/chat/team/chat', {
          conversation_id: conversationId,
          message: text,
          participant_id: activeMember.collaborator_id,
        });
      }

      // Update temp message with real data (team chat only)
      if (!isSupportChat && response?.data.messageData) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === tmpId
              ? {
                  ...m,
                  id: response.data.messageData.id,
                  status: 'sent' as const,
                  attachments: response.data.messageData.metadata?.attachments || [],
                }
              : m
          )
        );
      }
    } catch (err: any) {
      console.error('Send failed:', err);
      toast.error(err.response?.data?.error || 'Failed to send message');
      setMessages((prev) => prev.filter((m) => m.id !== tmpId));
      setInputMessage(text);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  }, [inputMessage, fileAttachments, activeMember, conversationId, sending, userInfo]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFileAttachments((prev) => [...prev, ...Array.from(e.target.files!)]);
    }
    e.target.value = '';
  };

  const removeAttachment = (index: number) => {
    setFileAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // ---------------------------------------------------------------------------
  // Filtered members
  // ---------------------------------------------------------------------------

  const filteredMembers = useMemo(() => {
    if (!memberSearch) return teamMembers;
    const q = memberSearch.toLowerCase();
    return teamMembers.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        m.email.toLowerCase().includes(q) ||
        m.role.toLowerCase().includes(q)
    );
  }, [teamMembers, memberSearch]);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  const statusColor = (s: string) => {
    if (s === 'online' || s === 'active') return colors.green;
    if (s === 'away') return colors.orange;
    return colors.muted;
  };

  const getInitials = (name: string) => {
    return name.split(' ').map((n) => n[0]).join('').toUpperCase().substring(0, 2);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div style={{ height: 'calc(100vh - 140px)', display: 'flex', flexDirection: 'column' }}>
    <SplitPanel
      leftWidth={320}
      left={
        <>
          {/* Header */}
          <div style={{
            padding: '16px', borderBottom: `1px solid ${colors.navy3}`,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <FontAwesomeIcon icon={faUsers} style={{ color: colors.gold }} />
            <span style={{ color: colors.white, fontWeight: 700, fontSize: 15, fontFamily: fonts.body }}>
              Team Members
            </span>
            <span style={{
              background: colors.navy3, color: colors.muted, borderRadius: 10,
              padding: '2px 8px', fontSize: 11, fontWeight: 600, marginLeft: 'auto',
            }}>
              {teamMembers.length}
            </span>
          </div>

          {/* Search */}
          <div style={{ padding: '8px 12px', borderBottom: `1px solid ${colors.navy3}` }}>
            <SearchBar
              value={memberSearch}
              onChange={setMemberSearch}
              placeholder="Search team members..."
            />
          </div>

          {/* Member list */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {/* KairosIQ Support — always at top */}
            <div style={{ borderBottom: `1px solid ${colors.navy3}` }}>
              <ListItem
                selected={activeMember?.id === '__kairosiq-support__'}
                onClick={() => startChat(supportMember)}
                title={
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: colors.gold, fontWeight: 700 }}>KairosIQ Support</span>
                    <FontAwesomeIcon icon={faCircle} style={{ fontSize: 7, color: colors.green }} />
                  </span>
                }
                subtitle="support@kairosiq.ai"
                meta="Support Team"
              />
            </div>

            {loadingMembers ? (
              <div style={{ padding: 32, textAlign: 'center', color: colors.muted }}>
                <FontAwesomeIcon icon={faSpinner} spin /> Loading...
              </div>
            ) : filteredMembers.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center', color: colors.muted }}>
                <FontAwesomeIcon icon={faUsers} style={{ fontSize: 28, marginBottom: 8, display: 'block' }} />
                <div style={{ fontSize: 13 }}>
                  {teamMembers.length === 0
                    ? 'No team members found in your brokerage'
                    : 'No matching members'}
                </div>
              </div>
            ) : (
              filteredMembers.map((member) => (
                <ListItem
                  key={member.id}
                  selected={activeMember?.id === member.id}
                  onClick={() => !member.isPending && startChat(member)}
                  title={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {member.name}
                      <FontAwesomeIcon
                        icon={faCircle}
                        style={{ fontSize: 7, color: statusColor(member.status) }}
                      />
                    </span>
                  }
                  subtitle={member.email}
                  meta={member.isPending ? 'Pending invitation' : member.role}
                  rightContent={
                    member.lastMessage ? (
                      <span style={{ fontSize: 10, color: colors.muted }}>
                        {member.lastMessageTime ? formatTime(member.lastMessageTime) : ''}
                      </span>
                    ) : null
                  }
                />
              ))
            )}
          </div>
        </>
      }
      right={
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {!activeMember ? (
            /* No member selected — welcome screen */
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', height: '100%', padding: 40, textAlign: 'center',
            }}>
              <div style={{
                width: 80, height: 80, borderRadius: '50%',
                background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 24, boxShadow: '0 4px 20px rgba(201,149,42,0.3)',
              }}>
                <FontAwesomeIcon icon={faComments} style={{ fontSize: 36, color: colors.navy }} />
              </div>
              <h2 style={{ color: colors.navy, fontFamily: fonts.display, fontSize: 28, margin: '0 0 8px' }}>
                Interoffice Chat
              </h2>
              <p style={{ color: colors.muted, fontSize: 14, maxWidth: 400, lineHeight: 1.6 }}>
                Select a team member from the left to start a conversation.
                Messages are delivered in real-time within your brokerage.
              </p>
            </div>
          ) : (
            <>
              {/* Chat header */}
              <div style={{
                padding: '14px 24px', borderBottom: `1px solid ${colors.border}`,
                background: '#fff', display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: colors.navy2, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  color: colors.gold, fontWeight: 700, fontSize: 14,
                  fontFamily: fonts.body,
                }}>
                  {getInitials(activeMember.name)}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: colors.navy, fontFamily: fonts.body }}>
                    {activeMember.name}
                  </div>
                  <div style={{ fontSize: 11, color: statusColor(activeMember.status), fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <FontAwesomeIcon icon={faCircle} style={{ fontSize: 6 }} />
                    {activeMember.status === 'active' || activeMember.status === 'online' ? 'Online' : 'Offline'}
                    <span style={{ color: colors.muted, marginLeft: 8 }}>{activeMember.role}</span>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
                {loadingMessages ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: colors.muted }}>
                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} /> Loading messages...
                  </div>
                ) : messages.length === 0 ? (
                  <div style={{ textAlign: 'center', color: colors.muted, marginTop: 60 }}>
                    <FontAwesomeIcon icon={faComments} style={{ fontSize: 32, marginBottom: 8 }} />
                    <div style={{ fontSize: 13 }}>No messages yet. Say hello!</div>
                  </div>
                ) : (
                  <>
                    {messages.map((msg) => {
                      const isMe = msg.sender === 'user';
                      return (
                        <div
                          key={msg.id}
                          style={{
                            display: 'flex', flexDirection: 'column',
                            alignItems: isMe ? 'flex-end' : 'flex-start',
                            marginBottom: 12,
                          }}
                        >
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3,
                            flexDirection: isMe ? 'row-reverse' : 'row',
                          }}>
                            <div style={{
                              width: 24, height: 24, borderRadius: '50%',
                              background: isMe ? colors.navy3 : colors.navy2,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: isMe ? colors.muted : colors.gold,
                              fontSize: 9, fontWeight: 700,
                            }}>
                              {isMe
                                ? getInitials(`${userInfo?.user_first_name || ''} ${userInfo?.user_last_name || ''}`)
                                : getInitials(activeMember.name)}
                            </div>
                            <span style={{ fontSize: 11, color: colors.muted, fontWeight: 600 }}>
                              {isMe ? 'You' : activeMember.firstName}
                            </span>
                            <span style={{ fontSize: 10, color: colors.muted }}>
                              {formatTime(msg.timestamp)}
                            </span>
                          </div>

                          <div style={{
                            maxWidth: '70%',
                            background: isMe ? colors.navy2 : '#fff',
                            color: isMe ? colors.white : colors.navy,
                            borderRadius: isMe ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                            overflow: 'hidden',
                            border: isMe ? 'none' : `1px solid ${colors.border}`,
                            boxShadow: isMe ? 'none' : '0 1px 3px rgba(0,0,0,0.05)',
                          }}>
                            {/* Inline image attachments — render above text */}
                            {msg.attachments && msg.attachments.length > 0 && (() => {
                              const images = msg.attachments.filter((a: any) => a.type === 'image' || a.mimetype?.startsWith('image/'));
                              const files = msg.attachments.filter((a: any) => !(a.type === 'image' || a.mimetype?.startsWith('image/')));
                              return (
                                <>
                                  {images.length > 0 && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                      {images.map((att: any, i: number) => {
                                        const fileKey = att.s3Key || att.savedFileName || att.filename || '';
                                        const fileName = att.name || att.originalName || 'Image';
                                        const url = getAuthUrl(`/api/chat/attachments/${encodeURIComponent(fileKey)}`);
                                        return (
                                          <a key={i} href={url} target="_blank" rel="noreferrer" style={{ display: 'block', lineHeight: 0 }}>
                                            <img src={url} alt={fileName} style={{
                                              width: '100%', maxHeight: 280, objectFit: 'cover',
                                              display: 'block', cursor: 'pointer',
                                            }} />
                                          </a>
                                        );
                                      })}
                                    </div>
                                  )}
                                  {files.length > 0 && (
                                    <div style={{ padding: '8px 14px 4px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                                      {files.map((att: any, i: number) => {
                                        const fileKey = att.s3Key || att.savedFileName || att.filename || '';
                                        const fileName = att.name || att.originalName || 'File';
                                        const url = getAuthUrl(`/api/chat/attachments/${encodeURIComponent(fileKey)}`);
                                        const isPdf = att.mimetype === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');
                                        const fileSize = att.size ? (att.size > 1048576 ? `${(att.size / 1048576).toFixed(1)} MB` : `${Math.round(att.size / 1024)} KB`) : '';
                                        return (
                                          <a key={i} href={url} target="_blank" rel="noreferrer" style={{
                                            display: 'flex', alignItems: 'center', gap: 10,
                                            padding: '10px 12px',
                                            background: isMe ? 'rgba(255,255,255,0.08)' : '#f8f9fa',
                                            borderRadius: 8, textDecoration: 'none',
                                            border: isMe ? '1px solid rgba(255,255,255,0.1)' : `1px solid ${colors.border}`,
                                          }}>
                                            <div style={{
                                              width: 36, height: 36, borderRadius: 8,
                                              background: isPdf ? '#e74c3c' : colors.gold,
                                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                                              flexShrink: 0,
                                            }}>
                                              <FontAwesomeIcon icon={isPdf ? faFilePdf : faFile} style={{ color: '#fff', fontSize: 16 }} />
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                              <div style={{
                                                fontSize: 13, fontWeight: 600,
                                                color: isMe ? '#fff' : colors.navy,
                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                              }}>{fileName}</div>
                                              {fileSize && <div style={{ fontSize: 11, color: colors.muted, marginTop: 1 }}>{fileSize}</div>}
                                            </div>
                                            <FontAwesomeIcon icon={faDownload} style={{ color: isMe ? colors.gold : colors.muted, fontSize: 14, flexShrink: 0 }} />
                                          </a>
                                        );
                                      })}
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                            {/* Text content */}
                            {msg.content && !(msg.attachments?.length > 0 && /^Sent \d+ file\(s\)$/.test(msg.content)) && (
                              <div style={{ padding: '10px 14px', fontSize: 14, lineHeight: 1.5, fontFamily: fonts.body, whiteSpace: 'pre-wrap' }}>
                                {msg.content}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    <div ref={chatEndRef} />
                  </>
                )}
              </div>

              {/* File attachment preview */}
              {fileAttachments.length > 0 && (
                <div style={{
                  padding: '8px 24px', borderTop: `1px solid ${colors.border}`,
                  background: colors.offBg, display: 'flex', gap: 8, flexWrap: 'wrap',
                }}>
                  {fileAttachments.map((file, i) => (
                    <div key={i} style={{
                      background: '#fff', border: `1px solid ${colors.border}`, borderRadius: 6,
                      padding: '4px 10px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                      <FontAwesomeIcon
                        icon={file.type.startsWith('image/') ? faImage : faFile}
                        style={{ color: colors.gold, fontSize: 10 }}
                      />
                      <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {file.name}
                      </span>
                      <button
                        onClick={() => removeAttachment(i)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.muted, padding: 0 }}
                      >
                        <FontAwesomeIcon icon={faTimes} style={{ fontSize: 10 }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Input area */}
              <div style={{
                padding: '16px 24px', borderTop: `1px solid ${colors.border}`,
                background: '#fff',
              }}>
                <div style={{
                  display: 'flex', gap: 8, alignItems: 'flex-end',
                  background: colors.offBg, borderRadius: 12,
                  border: `1px solid ${colors.border}`, padding: '8px 12px',
                }}>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: colors.muted, padding: '4px', flexShrink: 0,
                    }}
                    title="Attach file"
                  >
                    <FontAwesomeIcon icon={faPaperclip} />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  <textarea
                    ref={inputRef}
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    placeholder={`Message ${activeMember.firstName}...`}
                    rows={1}
                    style={{
                      flex: 1, border: 'none', background: 'transparent',
                      resize: 'none', outline: 'none', fontSize: 14,
                      fontFamily: fonts.body, color: colors.navy,
                      lineHeight: 1.5, maxHeight: 120,
                    }}
                    onInput={(e) => {
                      const el = e.currentTarget;
                      el.style.height = 'auto';
                      el.style.height = Math.min(el.scrollHeight, 120) + 'px';
                    }}
                  />
                  <button
                    onClick={handleSend}
                    disabled={sending || (!inputMessage.trim() && fileAttachments.length === 0)}
                    style={{
                      background: (inputMessage.trim() || fileAttachments.length > 0)
                        ? `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`
                        : colors.border,
                      border: 'none', borderRadius: 8,
                      width: 38, height: 38, cursor: (inputMessage.trim() || fileAttachments.length > 0) ? 'pointer' : 'default',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.15s', flexShrink: 0,
                    }}
                  >
                    <FontAwesomeIcon
                      icon={sending ? faSpinner : faPaperPlane}
                      spin={sending}
                      style={{
                        color: (inputMessage.trim() || fileAttachments.length > 0) ? colors.navy : colors.muted,
                        fontSize: 14,
                      }}
                    />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      }
    />
    </div>
  );
};

export default InterofficeChatTab;
